import { useState } from "react";
import { MapPin, Navigation, ExternalLink, Plus, Minus, Layers, Map as MapIcon, Phone } from "lucide-react";
import { BRAND, whatsappLink } from "@/lib/brand";

/**
 * GoogleMapSection — Premium Google Map for THE KC INDIA / Kedarnath Cottage.
 *
 * FEATURES
 *  · Toggle between Roadmap and Satellite view
 *  · Zoom In / Zoom Out controls
 *  · "Get Directions" — deep link to Google Maps directions
 *  · "Open in Google Maps" — deep link to the location
 *  · Address card + call / WhatsApp
 *  · Fully responsive
 *
 * DATA SOURCE
 * Uses the classic Google Maps embed (no API key required) — reliable and free.
 * When you later add a Google Maps Embed API key (see /app/backend/.env),
 * the URL builder can be swapped to the Maps Embed API for advanced features.
 */

const PLACE_QUERY = encodeURIComponent(
  "Kedarnath Cottage, Near Heritage Aviation Helipad, Sersi, Rudraprayag, Uttarakhand 246471"
);

// Classic Google Maps embed URL builder — supports maptype (t=m/k), zoom (z=1..20)
const embedUrl = ({ mapType, zoom }) =>
  `https://maps.google.com/maps?q=${PLACE_QUERY}&t=${mapType === "satellite" ? "k" : "m"}&z=${zoom}&ie=UTF8&iwloc=&output=embed`;

const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${PLACE_QUERY}&travelmode=driving`;
const openInMapsUrl = `https://www.google.com/maps/search/?api=1&query=${PLACE_QUERY}`;

export const GoogleMapSection = () => {
  const [mapType, setMapType] = useState("roadmap"); // roadmap | satellite
  const [zoom, setZoom] = useState(14);

  const zoomIn = () => setZoom((z) => Math.min(20, z + 2));
  const zoomOut = () => setZoom((z) => Math.max(4, z - 2));

  return (
    <section className="bg-kc section-pad" data-testid="google-map-section">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        {/* ========= HEADER ========= */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-end mb-10 md:mb-14">
          <div className="md:col-span-7">
            <div className="overline">Find the Cottage</div>
            <h2 className="font-serif-display text-4xl md:text-6xl mt-6 font-light leading-[1.02] tracking-[-0.02em]">
              Just <span className="italic text-gold">20 metres</span> <br className="hidden md:block" />
              from Heritage Aviation.
            </h2>
          </div>
          <p className="md:col-span-5 text-ink-soft text-[15px] leading-[1.8]">
            Sersi lies on the Kedarnath yatra route in Rudraprayag district — a quiet Himalayan village blessed by the Mandakini river, and the closest luxury base to Kedarnath.
          </p>
        </div>

        {/* ========= MAP + ADDRESS PANEL ========= */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Address card */}
          <aside className="lg:col-span-4 bg-[#0F0F0F] text-[#F8F6F2] p-8 md:p-10 flex flex-col" data-testid="map-address-panel">
            <MapPin size={22} className="text-gold" strokeWidth={1.4} />
            <div className="overline no-line text-gold mt-8">The Cottage</div>
            <div className="font-serif-display text-3xl md:text-4xl mt-4 leading-tight">{BRAND.business}</div>
            <p className="mt-6 text-[14px] leading-[1.75] text-[#F8F6F2]/80">
              {BRAND.address}
            </p>

            <div className="mt-10 pt-8 border-t border-[#D4AF37]/25 space-y-5 text-[13px]">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Coordinates</div>
                <div className="font-serif-display text-lg mt-1.5">30.6373° N · 79.0537° E</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Nearest Helipad</div>
                <div className="font-serif-display text-lg mt-1.5">Heritage Aviation · Sersi</div>
                <div className="text-[11px] text-[#F8F6F2]/60 mt-1">Approx. 20 metres · walking distance</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Distance</div>
                <div className="font-serif-display text-lg mt-1.5">Sonprayag · 15 km</div>
                <div className="text-[11px] text-[#F8F6F2]/60 mt-1">Gaurikund 20 km · Kedarnath temple 40 km</div>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-[#D4AF37]/25 flex flex-col gap-3">
              <a
                href={directionsUrl}
                target="_blank"
                rel="noreferrer"
                className="btn-gold w-full justify-center"
                data-testid="map-directions-btn"
              >
                <Navigation size={14} /> Get Directions
              </a>
              <a
                href={openInMapsUrl}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost-light w-full justify-center"
                data-testid="map-open-btn"
              >
                <ExternalLink size={14} /> Open in Google Maps
              </a>
              <a
                href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
                className="flex items-center justify-center gap-3 mt-2 text-[11px] uppercase tracking-[0.28em] text-[#F8F6F2]/70 hover:text-gold transition-colors"
                data-testid="map-call"
              >
                <Phone size={13} /> Call for road status · {BRAND.phone}
              </a>
            </div>
          </aside>

          {/* Map */}
          <div className="lg:col-span-8 relative border border-kc bg-surface overflow-hidden" data-testid="map-frame">
            {/* Iframe */}
            <div className="relative w-full aspect-[16/11] md:aspect-[16/10]">
              <iframe
                key={`${mapType}-${zoom}`}
                title="Kedarnath Cottage · Google Map"
                src={embedUrl({ mapType, zoom })}
                className="absolute inset-0 w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                data-testid="map-iframe"
              />
            </div>

            {/* Floating controls — top right */}
            <div className="absolute top-4 right-4 flex flex-col gap-2" data-testid="map-controls">
              {/* Layer toggle */}
              <div className="bg-[#0F0F0F]/90 backdrop-blur border border-gold/30 flex" role="group" aria-label="Map view">
                <button
                  onClick={() => setMapType("roadmap")}
                  className={`flex items-center gap-1.5 px-3 py-2 text-[9px] uppercase tracking-[0.22em] transition-colors ${
                    mapType === "roadmap" ? "bg-gold text-[#0F0F0F]" : "text-[#F8F6F2] hover:text-gold"
                  }`}
                  data-testid="map-toggle-roadmap"
                  aria-pressed={mapType === "roadmap"}
                >
                  <MapIcon size={11} /> Map
                </button>
                <button
                  onClick={() => setMapType("satellite")}
                  className={`flex items-center gap-1.5 px-3 py-2 text-[9px] uppercase tracking-[0.22em] transition-colors border-l border-gold/30 ${
                    mapType === "satellite" ? "bg-gold text-[#0F0F0F]" : "text-[#F8F6F2] hover:text-gold"
                  }`}
                  data-testid="map-toggle-satellite"
                  aria-pressed={mapType === "satellite"}
                >
                  <Layers size={11} /> Satellite
                </button>
              </div>

              {/* Zoom */}
              <div className="bg-[#0F0F0F]/90 backdrop-blur border border-gold/30 flex flex-col" role="group" aria-label="Zoom">
                <button
                  onClick={zoomIn}
                  className="p-2 text-[#F8F6F2] hover:text-gold transition-colors"
                  aria-label="Zoom in"
                  data-testid="map-zoom-in"
                >
                  <Plus size={14} />
                </button>
                <div className="h-px bg-gold/30" />
                <button
                  onClick={zoomOut}
                  className="p-2 text-[#F8F6F2] hover:text-gold transition-colors"
                  aria-label="Zoom out"
                  data-testid="map-zoom-out"
                >
                  <Minus size={14} />
                </button>
              </div>
            </div>

            {/* Bottom bar — quick actions */}
            <div className="border-t border-kc bg-cream px-5 md:px-8 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-[11px] uppercase tracking-[0.28em] text-ink-soft flex items-center gap-3">
                <span className="w-2 h-2 bg-gold rounded-full animate-pulse" />
                Live location · Zoom {zoom}× · {mapType === "satellite" ? "Satellite" : "Roadmap"}
              </div>
              <div className="flex items-center gap-3">
                <a
                  href={directionsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] uppercase tracking-[0.28em] text-ink hover:text-gold transition-colors flex items-center gap-1.5"
                  data-testid="map-directions-mini"
                >
                  <Navigation size={12} /> Directions
                </a>
                <span className="text-ink/30">·</span>
                <a
                  href={openInMapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] uppercase tracking-[0.28em] text-ink hover:text-gold transition-colors flex items-center gap-1.5"
                  data-testid="map-open-mini"
                >
                  <ExternalLink size={12} /> Open in Maps
                </a>
                <span className="text-ink/30">·</span>
                <a
                  href={whatsappLink("Namaste 🙏 Could you please share directions to Kedarnath Cottage?")}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] uppercase tracking-[0.28em] text-ink hover:text-gold transition-colors"
                  data-testid="map-whatsapp"
                >
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
